/**
 * 업체 상세.
 * 정보·계약 탭은 Figma 2열(기본 정보 / 계약 정보) + 계약 변경 이력 카드를 따른다.
 * 소속 사용자·담당 거래처 탭은 API가 준비될 때까지 비활성화한다.
 */
import {
  partnerCompanyService,
  type PartnerCompanyDocumentType,
} from '@/entities/partner-company/api/partner-company.api';
import {
  useCreatePartnerCompanyContract,
  useCreatePartnerCompanyDocument,
  usePartnerCompanyDetail,
  usePartnerCompanyHistories,
  useUpdatePartnerCompany,
  useUpdatePartnerCompanyContract,
} from '@/entities/partner-company/hooks/usePartnerCompanies';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { DataCard } from '@/shared/components/DataCard';
import { DateRangePicker } from '@/shared/components/DatePicker';
import { EmptyState } from '@/shared/components/EmptyState';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { FooterRow, ToolbarRow } from '@/shared/components/ui/row';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Briefcase } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  PARTNER_COMPANY_DOCUMENT_SLOTS,
  buildPartnerCompanyContractPeriodBody,
  didPartnerCompanyContractPeriodChange,
  getLatestPartnerCompanyContract,
  resolvePartnerCompanyType,
} from './partnerCompanyForm';
import { mapPartnerCompanyHistoriesToRows } from './partnerCompanyHistory';

const DETAIL_TABS = [
  { key: 'info', label: '정보 · 계약', disabled: false },
  { key: 'users', label: '소속 사용자', disabled: true },
  { key: 'clients', label: '담당 거래처', disabled: true },
] as const;

export const PartnerCompanyDetailPage: React.FC = () => {
  const { readOnly } = useMenuAccess();
  const toast = useToast();
  const navigate = useNavigate();
  const { companyId: idParam } = useParams({ strict: false }) as {
    companyId?: string;
  };
  const parsedCompanyId = Number(idParam);
  const partnerCompanyId = Number.isFinite(parsedCompanyId)
    ? parsedCompanyId
    : null;

  const { data, isLoading, isError, error } =
    usePartnerCompanyDetail(partnerCompanyId);
  const { data: histories = [] } = usePartnerCompanyHistories(partnerCompanyId);
  const updatePartnerCompany = useUpdatePartnerCompany();
  const updateContract = useUpdatePartnerCompanyContract();
  const createContract = useCreatePartnerCompanyContract();
  const createDocument = useCreatePartnerCompanyDocument();

  const [terminateOpen, setTerminateOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [representativeName, setRepresentativeName] = useState('');
  const [csoReportNumber, setCsoReportNumber] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [contractStartDate, setContractStartDate] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');

  useEffect(() => {
    if (isError && error) {
      handleApiError(error, toast);
    }
  }, [error, isError, toast]);

  useEffect(() => {
    if (!data) return;
    const latestContract = getLatestPartnerCompanyContract(data.contracts);
    setCompanyName(data.companyName ?? '');
    setRepresentativeName(data.representativeName ?? '');
    setCsoReportNumber(data.csoReportNumber ?? '');
    setContactNumber(data.contactNumber ?? '');
    setEmail(data.email ?? '');
    setAddress(data.address ?? '');
    setBankName(data.bankName ?? '');
    setAccountNumber(data.accountNumber ?? '');
    setAccountHolder(data.accountHolder ?? '');
    setContractStartDate(latestContract?.startDate ?? '');
    setContractEndDate(latestContract?.endDate ?? '');
  }, [data]);

  if (!Number.isFinite(parsedCompanyId)) {
    return (
      <div className="space-y-5">
        <PageHeader icon={Briefcase} title="업체 상세" />
        <EmptyState description="업체를 찾을 수 없습니다." />
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate({ to: ROUTE_PATHS.PARTNER_COMPANY } as never)}
        >
          나가기
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="업체 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-5">
        <PageHeader icon={Briefcase} title="업체 상세" />
        <EmptyState description="업체를 찾을 수 없습니다." />
      </div>
    );
  }

  const contractHistoryRows = mapPartnerCompanyHistoriesToRows(histories);

  const handleSaveBasicInfo = async () => {
    const period = buildPartnerCompanyContractPeriodBody(
      contractStartDate,
      contractEndDate,
    );
    const latestContract = getLatestPartnerCompanyContract(data.contracts);
    const periodChanged = didPartnerCompanyContractPeriodChange(
      latestContract,
      contractStartDate,
      contractEndDate,
    );

    try {
      const nextCompanyName = companyName.trim();
      await updatePartnerCompany.mutateAsync({
        partnerCompanyId: data.partnerCompanyId,
        data: {
          companyName: nextCompanyName,
          companyType: resolvePartnerCompanyType(nextCompanyName),
          representativeName,
          csoReportNumber,
          contactNumber,
          email,
          address,
          bankName,
          accountNumber,
          accountHolder,
        },
      });

      if (period.startDate && periodChanged) {
        if (latestContract) {
          await updateContract.mutateAsync({
            partnerCompanyId: data.partnerCompanyId,
            contractId: latestContract.contractId,
            data: period,
          });
        } else {
          await createContract.mutateAsync({
            partnerCompanyId: data.partnerCompanyId,
            data: {
              contractNumber: `C-${data.businessRegistrationNumber.replace(/-/g, '')}`,
              contractType: 'DEFAULT',
              ...period,
            },
          });
        }
      }

      toast.success('업체 정보를 저장했습니다.');
    } catch (saveError) {
      handleApiError(saveError, toast);
    }
  };

  const handleTerminate = async () => {
    try {
      await updatePartnerCompany.mutateAsync({
        partnerCompanyId: data.partnerCompanyId,
        data: { active: false },
      });
      toast.success('업체를 비활성화했습니다.');
    } catch (saveError) {
      handleApiError(saveError, toast);
    } finally {
      setTerminateOpen(false);
    }
  };

  const handleDocumentUpload = async (
    documentType: PartnerCompanyDocumentType,
    file: File,
  ) => {
    try {
      const uploadInfo = await partnerCompanyService.getDocumentUploadUrl(
        file.name,
      );
      const uploadResponse = await fetch(uploadInfo.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!uploadResponse.ok) {
        throw new Error('파일 업로드에 실패했습니다.');
      }
      await createDocument.mutateAsync({
        partnerCompanyId: data.partnerCompanyId,
        data: {
          documentType,
          originalFileName: file.name,
          s3FileKey: uploadInfo.uploadKey,
        },
      });
      toast.success('문서를 업로드했습니다.');
    } catch (uploadError) {
      handleApiError(uploadError, toast);
    }
  };

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Briefcase}
        title={data.companyName}
        description="업체 상세"
        actions={
          <ToolbarRow className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigate({ to: ROUTE_PATHS.PARTNER_COMPANY } as never)
              }
            >
              나가기
            </Button>
            <MenuWriteGate>
              <Button
                type="button"
                variant="destructive"
                disabled={readOnly || !data.active}
                onClick={() => setTerminateOpen(true)}
              >
                계약 해지
              </Button>
            </MenuWriteGate>
          </ToolbarRow>
        }
      />

      <div className="flex gap-1 border-b">
        {DETAIL_TABS.map((tab) => (
          <Button
            key={tab.key}
            type="button"
            variant={tab.key === 'info' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-b-none"
            disabled={tab.disabled}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>업체 정보 · 계약</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-[60px]">
            <section className="space-y-2.5">
              <p className="text-sm font-medium text-muted-foreground">
                기본 정보
              </p>
              <DetailFieldRow label="업체 ID">
                <Input
                  className="w-full sm:w-[320px]"
                  disabled
                  value={String(data.partnerCompanyId)}
                />
              </DetailFieldRow>
              <DetailFieldRow label="업체명">
                <Input
                  className="w-full sm:w-[320px]"
                  disabled={readOnly}
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                />
              </DetailFieldRow>
              <DetailFieldRow label="사업자번호">
                <Input
                  className="w-full sm:w-[320px]"
                  disabled
                  value={data.businessRegistrationNumber}
                />
              </DetailFieldRow>
              <DetailFieldRow label="대표자">
                <Input
                  className="w-full sm:w-[320px]"
                  disabled={readOnly}
                  value={representativeName}
                  onChange={(event) =>
                    setRepresentativeName(event.target.value)
                  }
                />
              </DetailFieldRow>
              <DetailFieldRow label="CSO 신고번호">
                <Input
                  className="w-full sm:w-[320px]"
                  disabled={readOnly}
                  value={csoReportNumber}
                  onChange={(event) => setCsoReportNumber(event.target.value)}
                />
              </DetailFieldRow>
              <DetailFieldRow label="연락처">
                <Input
                  className="w-full sm:w-[320px]"
                  disabled={readOnly}
                  value={contactNumber}
                  onChange={(event) => setContactNumber(event.target.value)}
                />
              </DetailFieldRow>
              <DetailFieldRow label="정산 계좌">
                <div className="flex w-full gap-2 sm:w-[320px]">
                  <Input
                    className="w-[128px]"
                    disabled={readOnly}
                    value={bankName}
                    onChange={(event) => setBankName(event.target.value)}
                    placeholder="은행명"
                  />
                  <Input
                    className="min-w-0 flex-1"
                    disabled={readOnly}
                    value={accountNumber}
                    onChange={(event) => setAccountNumber(event.target.value)}
                    placeholder="계좌번호"
                  />
                </div>
              </DetailFieldRow>
            </section>

            <section className="space-y-2.5">
              <p className="text-sm font-medium text-muted-foreground">
                계약 정보
              </p>
              <DetailFieldRow label="계약 기간">
                <DateRangePicker
                  className="w-full sm:w-[360px]"
                  value={{ from: contractStartDate, to: contractEndDate }}
                  onChange={({ from, to }) => {
                    setContractStartDate(from);
                    setContractEndDate(to);
                  }}
                  disabled={readOnly}
                  placeholder="날짜 범위 선택"
                />
              </DetailFieldRow>
              {PARTNER_COMPANY_DOCUMENT_SLOTS.map((slot) => {
                const uploaded = [...data.documents]
                  .reverse()
                  .find((document) => document.documentType === slot.type);
                return (
                  <PartnerCompanyDocumentActionRow
                    key={slot.type}
                    label={slot.label}
                    fileName={uploaded?.originalFileName}
                    downloadUrl={uploaded?.downloadUrl}
                    disabled={readOnly}
                    onFileSelected={(file) =>
                      void handleDocumentUpload(slot.type, file)
                    }
                  />
                );
              })}
            </section>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-sm font-bold text-foreground">
          계약 변경 이력 (버전 보관 · 지출보고서 대응)
        </h2>
        <DataCard>
          {contractHistoryRows.length === 0 ? (
            <EmptyState description="변경 이력이 없습니다." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">버전</TableHead>
                  <TableHead className="w-[100px]">변경 구분</TableHead>
                  <TableHead className="min-w-[120px]">변경 내용</TableHead>
                  <TableHead className="min-w-[180px]">변경 전</TableHead>
                  <TableHead className="min-w-[180px]">변경 후</TableHead>
                  <TableHead className="w-[90px]">적용 시작일</TableHead>
                  <TableHead className="w-[70px]">변경자</TableHead>
                  <TableHead className="w-[120px]">변경일시</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractHistoryRows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.versionLabel}</TableCell>
                    <TableCell>{row.changeType}</TableCell>
                    <TableCell className="whitespace-pre-line">
                      {row.changeContent}
                    </TableCell>
                    <TableCell className="whitespace-pre-line">
                      {row.before}
                    </TableCell>
                    <TableCell className="whitespace-pre-line">
                      {row.after}
                    </TableCell>
                    <TableCell>{row.effectiveStartDate}</TableCell>
                    <TableCell>{row.changedBy}</TableCell>
                    <TableCell>{row.changedAt}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DataCard>
      </div>

      {!readOnly ? (
        <FooterRow>
          <Button
            type="button"
            className="ruda-button"
            onClick={handleSaveBasicInfo}
            disabled={
              updatePartnerCompany.isPending ||
              updateContract.isPending ||
              createContract.isPending
            }
          >
            저장
          </Button>
        </FooterRow>
      ) : null}

      <AlertDialog open={terminateOpen} onOpenChange={setTerminateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>계약 해지</AlertDialogTitle>
            <AlertDialogDescription>
              {data.companyName} 업체를 비활성화할까요?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleTerminate()}>
              해지
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const DetailFieldRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  // 모바일은 라벨 위·입력 아래로 쌓는다
  <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
    <Label className="w-[95px] shrink-0 text-muted-foreground">{label}</Label>
    {children}
  </div>
);

/** 상세 계약 정보 — 파일명 + 보기/업로드 */
const PartnerCompanyDocumentActionRow: React.FC<{
  label: string;
  fileName?: string | null;
  downloadUrl?: string | null;
  disabled?: boolean;
  onFileSelected: (file: File) => void;
}> = ({ label, fileName, downloadUrl, disabled, onFileSelected }) => (
  <div className="flex flex-wrap items-center gap-3">
    <p className="w-full shrink-0 text-sm text-muted-foreground sm:w-[95px]">
      {label}
    </p>
    <Input
      className="w-full min-w-0 flex-1 sm:w-[220px] sm:flex-none"
      disabled
      value={fileName ?? ''}
      placeholder="선택된 파일 없음"
    />
    {downloadUrl ? (
      <Button type="button" variant="outline" size="sm" asChild>
        <a href={downloadUrl} target="_blank" rel="noreferrer">
          보기
        </a>
      </Button>
    ) : (
      <Button type="button" variant="outline" size="sm" disabled>
        보기
      </Button>
    )}
    <FileUploadButton
      accept=".pdf,.jpg,.jpeg,.png"
      disabled={disabled}
      showUploadIcon={false}
      onFilesSelected={(files) => {
        const file = files?.[0];
        if (file) onFileSelected(file);
      }}
    >
      업로드
    </FileUploadButton>
  </div>
);
